// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import Modal from '../../../../elements/Modal/Modal';
import Loading from '../../../../elements/Loading/Loading';
import PaymentModal from './PaymentModal/PaymentModal';

import commonStyle from '../../PreferencesViewCommon.scss';
import icons from '../../../../assets/styles/icons.scss';
import modalStyle from '../../../../assets/styles/modal.scss';
import buttonStyle from '../../../../assets/styles/button.scss';

import type { Payment } from '../../../../entities';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +postPayment: (payment: Payment) => void;
  +editPayment: (payment: Payment) => void;
  +deletePayment: (id: string) => void;
  +payments?: Payment[];
  +closePaymentModal: boolean;
  +successDelete?: boolean;
  +isPostingPayment?: boolean;
  +isPuttingPayment?: boolean;
  +isDeletingPayment?: boolean;
  +localValues: LocalValuesState;
};

type State = {
  paymentModalVisible: boolean;
  deleteModalVisible: boolean;
  cleanModalState?: boolean;
  payment?: Payment;
  paymentId: string;
};

const messages = defineMessages({
  title: {
    id: 'preferences.payments.document.title',
  },
  description: {
    id: 'preferences.payments.document.description',
  },
  payments: {
    id: 'preferences.payments.payments',
  },
  addNewPayment: {
    id: 'preferences.payments.addNewPayment',
  },
  deleteWarning: {
    id: 'preferences.payments.deleteWarning',
  },
  delete: {
    id: 'preferences.payments.delete',
  },
  edit: {
    id: 'preferences.payments.edit',
  },
  button: {
    cancel: {
      id: 'preferences.payments.button.cancel',
    },
    delete: {
      id: 'preferences.payments.button.delete',
    },
  }
});

export default class Payments extends Component<Props, State> {
  state = {
    paymentModalVisible: false,
    deleteModalVisible: false,
    cleanModalState: false,
    payment: null,
    paymentId: ''
  };

  componentWillReceiveProps(nextProps: Props) {
    const { paymentModalVisible, deleteModalVisible } = this.state;
    if (nextProps.closePaymentModal && paymentModalVisible) {
      this.setState({
        paymentModalVisible: false,
        cleanModalState: true,
        payment: null,
      });
    }
    if (nextProps.successDelete && deleteModalVisible) {
      this.setState({
        deleteModalVisible: false,
      });
    }
  }

  handleOpenPaymentModal() {
    this.setState({
      paymentModalVisible: true,
      cleanModalState: false,
    });
  }

  handleClosePaymentModal() {
    this.setState({
      paymentModalVisible: false,
      cleanModalState: true,
      payment: null,
    });
  }

  handleOpenDeleteModal(paymentId: string) {
    this.setState({
      deleteModalVisible: true,
      paymentId,
    });
  }

  handleCloseDeleteModal() {
    this.setState({
      deleteModalVisible: false,
    });
  }

  handleEdit(payment: Payment) {
    this.setState({
      paymentModalVisible: true,
      cleanModalState: false,
      payment,
    });
  }

  handleDelete(paymentId: string) {
    const { deletePayment } = this.props;
    deletePayment(paymentId);
  }

  render() {
    const { payments, postPayment, editPayment, isPostingPayment, isPuttingPayment, isDeletingPayment, localValues } = this.props;
    const { paymentModalVisible, deleteModalVisible, cleanModalState, payment, paymentId } = this.state;
    const paymentsContent = payments && payments.map((payment: Payment, n: number) => (
      <article key={n} className={commonStyle.preferenceBox}>
        <p className={commonStyle.name}>{payment.cardNumber}</p>
        <p>{payment.expireMonth}/{payment.expireYear}</p>
        <section className={commonStyle.boxBottom}>
          <FormattedMessage id={messages.delete.id}>{
            txt => <p className={commonStyle.delete}
                      onClick={this.handleOpenDeleteModal.bind(this, payment.id)}>{txt}</p>
          }</FormattedMessage>
          <FormattedMessage id={messages.edit.id}>{
            txt => <p className={commonStyle.edit}
                      onClick={this.handleEdit.bind(this, payment)}>{txt}</p>
          }</FormattedMessage>
        </section>
      </article>));
    return [
      <Helmet key="helmet">
        <title>{this.context.intl.formatMessage(messages.title)}</title>
        <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
      </Helmet>,
      <section key="payments" className={commonStyle.contentWrap}>
        <FormattedMessage id={messages.payments.id}>{
          txt => <h3>{txt}</h3>
        }</FormattedMessage>
        <section className={commonStyle.boxesWrap}>
          <article className={classNames(commonStyle.preferenceBox, commonStyle.firstBox)}
                   onClick={this.handleOpenPaymentModal.bind(this)}>
            <i className={icons.iconLocationMedium}/>
            <FormattedMessage id={messages.addNewPayment.id}>{
              txt => <p className={commonStyle.newBox}>{txt}</p>
            }</FormattedMessage>
          </article>
          {paymentsContent}
        </section>
      </section>,
      <PaymentModal key="paymentModal"
                    handleClose={this.handleClosePaymentModal.bind(this)}
                    paymentModalVisible={paymentModalVisible}
                    cleanModalState={cleanModalState}
                    payment={payment}
                    postPayment={postPayment}
                    editPayment={editPayment}
                    isPosting={isPostingPayment}
                    isPutting={isPuttingPayment}
                    localValues={localValues}/>,
      <Modal key="deleteModal"
             open={deleteModalVisible}
             height={180}
             handleClose={this.handleCloseDeleteModal.bind(this)}>
        {isDeletingPayment && <Loading type="sk-three-bounce"
                                       className={commonStyle.modalLoading}
                                       number={3}/>}
        {!isDeletingPayment && [
          <i key="icon" className={classNames(icons.symbolExclamationCircle, modalStyle.title)}/>,
          <FormattedMessage id={messages.deleteWarning.id} key="content">{
            txt => <p className={modalStyle.content}>{txt}</p>
          }</FormattedMessage>,
          <section className={modalStyle.buttonsWrap} key="wrap">
            <FormattedMessage id={messages.button.cancel.id}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey)}
                             onClick={this.handleCloseDeleteModal.bind(this)}
              >{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id={messages.button.delete.id}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.red)}
                             onClick={() => this.handleDelete(paymentId)}
              >{txt}</button>
            }</FormattedMessage>
          </section>,
        ]}
      </Modal>
    ]
  }
}

Payments.propTypes = {
  postPayment: PropTypes.func.isRequired,
  editPayment: PropTypes.func.isRequired,
  deletePayment: PropTypes.func.isRequired,
  payments: PropTypes.array,
  closePaymentModal: PropTypes.bool,
  isPostingPayment: PropTypes.bool,
  isPuttingPayment: PropTypes.bool,
  isDeletingPayment: PropTypes.bool,
  localValues: PropTypes.object.isRequired,
};

Payments.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/payments/components/Payments.jsx